import { type Static, Type } from "typebox";

import { defineRoute } from "./route.js";
import {
  DateOnly,
  errors,
  Exact,
  Identifier,
  Nullable,
  StringEnum,
  Timestamp,
} from "./schema.js";

// --- Safety recalls (VEH-027) -------------------------------------------------

/**
 * What the company knows about a recall on one vehicle. Every recall starts
 * as "may_apply": the source lists recalls by make, model and year, so only
 * a person who checked can say it was fixed or does not apply.
 */
export const vehicleRecallStatuses = [
  "may_apply",
  "scheduled",
  "repaired",
  "does_not_apply",
] as const;
export const VehicleRecallStatus = StringEnum(vehicleRecallStatuses);
export type VehicleRecallStatus = Static<typeof VehicleRecallStatus>;

export const VehicleRecall = Exact({
  /**
   * From a company other than the vehicle's maker, such as an upfitter or a
   * conversion. Listed apart and not counted as open: most vehicles of the
   * model never had that equipment.
   */
  addOnEquipment: Type.Boolean(),
  campaignNumber: Type.String(),
  /** What fails, as the source words it. */
  component: Nullable(Type.String()),
  /**
   * The recall's title in plain words, such as "Backup camera", never the
   * same as another recall's on this vehicle.
   */
  componentLabel: Type.String(),
  consequence: Nullable(Type.String()),
  /** The source's remedy says the repair costs nothing. */
  freeRepair: Type.Boolean(),
  id: Identifier,
  manufacturer: Nullable(Type.String()),
  note: Nullable(Type.String()),
  /** The source says not to drive it until it is fixed. */
  parkIt: Type.Boolean(),
  /** The source says to park it outside, away from buildings. */
  parkOutside: Type.Boolean(),
  remedy: Nullable(Type.String()),
  reportDate: Nullable(DateOnly),
  /** The logged service that fixed it, when someone linked one. */
  serviceRecord: Nullable(Exact({ completedAt: Timestamp, id: Identifier })),
  source: Type.String(),
  status: VehicleRecallStatus,
  statusChangedAt: Nullable(Timestamp),
  statusChangedByName: Nullable(Type.String()),
  summary: Nullable(Type.String()),
  version: Type.Integer(),
});
export type VehicleRecall = Static<typeof VehicleRecall>;

export const VehicleRecallList = Exact({
  /** When the source last answered for this make, model and year. */
  checkedAt: Nullable(Timestamp),
  items: Type.Array(VehicleRecall),
  /** False until the vehicle has a make, model and year to check. */
  modelKnown: Type.Boolean(),
  /** The vehicle's make as entered, for "call a Ford dealer". */
  vehicleMake: Nullable(Type.String()),
  /** Whether the vehicle has a VIN, so the lookup checks it exactly. */
  vinKnown: Type.Boolean(),
  /** Where to check this VIN's open recalls on nhtsa.gov. */
  vinLookupUrl: Type.String(),
});
export type VehicleRecallList = Static<typeof VehicleRecallList>;

export const listVehicleRecalls = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/:vehicleId/recalls",
  response: { 200: VehicleRecallList, ...errors(401, 403, 404) },
  tags: ["recalls"],
});

/**
 * Marks one recall. A service can be linked only when it was repaired, and
 * only one logged for this vehicle. 409 is a stale version.
 */
export const updateVehicleRecall = defineRoute({
  body: Exact({
    note: Type.Optional(Nullable(Type.String({ maxLength: 2_000 }))),
    serviceRecordId: Type.Optional(Nullable(Identifier)),
    status: VehicleRecallStatus,
    version: Type.Integer({ minimum: 1 }),
  }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/recalls/:recallId/status",
  response: { 200: VehicleRecallList, ...errors(401, 403, 404, 409, 422) },
  tags: ["recalls"],
});

export const RecallSummary = Exact({
  /**
   * Recalls not yet marked repaired or not applicable, across the fleet,
   * leaving out add-on equipment recalls.
   */
  openRecalls: Type.Integer(),
  /** Vehicles on the road with at least one of them. */
  vehiclesWithOpenRecalls: Type.Integer(),
});
export type RecallSummary = Static<typeof RecallSummary>;

export const getRecallSummary = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/recalls/summary",
  response: { 200: RecallSummary, ...errors(401, 403) },
  tags: ["recalls"],
});
